import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Crosshair } from 'lucide-react';
import { api as defaultApi } from '../services/api';

export interface AddressSuggestion {
  label: string; address: string; city: string;
  state: string; zip: string; lat: number | null; lng: number | null;
  place_id?: string | null; miles?: number | null; source?: string;
}

/**
 * The address box, everywhere a person is added.
 *
 * Owner, Sep 18 2026: on the phone the old list "hides at the top of the
 * screen" — it was pinned to where the box was before the keyboard opened.
 * The matches now sit in the page right under the box, so they scroll with
 * it and are always thumb-sized. The server ranks them nearest to home, so
 * "Mineral Wells" is the Texas one and a street name is the Leander one.
 *
 * Reports whether the text was PICKED from a match or free-typed, so a form
 * can say "we'll pin it later" instead of saving a pin nobody chose.
 */
export default function AddressAutocomplete({
  value, onChange, onSelect, placeholder, className, autoFocus, api = defaultApi, inputMode,
  suggestPath = '/properties/address-suggest', placePath = '/properties/address-place',
}: {
  value: string;
  onChange: (val: string) => void;
  onSelect: (s: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  api?: { get: (url: string, cfg?: any) => Promise<any> };
  inputMode?: 'text' | 'search';
  /** A public page (the referral form) uses the rate-limited portal lookups instead. */
  suggestPath?: string;
  placePath?: string;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pinning, setPinning] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const seq = useRef(0);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    onChange(v);
    setProblem(null);
    clearTimeout(timerRef.current);
    if (v.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    const mine = ++seq.current;
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(suggestPath, { params: { q: v } });
        if (mine !== seq.current) return;          // a newer keystroke is in flight
        const sugg: AddressSuggestion[] = r.data.suggestions || [];
        setSuggestions(sugg);
        setOpen(sugg.length > 0);
      } catch {
        if (mine === seq.current) { setSuggestions([]); setOpen(false); }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 300);
  }

  async function handleSelect(s: AddressSuggestion) {
    setOpen(false);
    // A Google match has no pin until it is picked.
    if ((s.lat == null || s.lng == null) && s.place_id) {
      setPinning(s.label);
      try {
        const r = await api.get(placePath, { params: { place_id: s.place_id } });
        const full: AddressSuggestion = r.data.suggestion;
        setSuggestions([]);
        onSelect(full);
      } catch {
        setProblem('Could not pin that one — try the next match, or type the full address.');
        setOpen(true);
      } finally { setPinning(null); }
      return;
    }
    setSuggestions([]);
    onSelect(s);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && open && suggestions[0]) { e.preventDefault(); handleSelect(suggestions[0]); }
    if (e.key === 'Escape') setOpen(false);
  }

  const secondLine = (s: AddressSuggestion) => {
    // A town-only match already says the town on its first line.
    const where = s.address ? [s.city, [s.state, s.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '';
    const far = s.miles != null && s.miles > 25 ? `${Math.round(s.miles)} mi from home` : null;
    return [where, far].filter(Boolean).join(' · ');
  };

  return (
    <div>
      <div className="relative">
        <input
          value={value}
          onChange={handleChange}
          onKeyDown={onKey}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          className={className || 'input'}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode={inputMode || 'text'}
          autoFocus={autoFocus}
        />
        {(loading || pinning) && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Crosshair className="h-3.5 w-3.5 text-gray-500 animate-spin" />
          </span>
        )}
      </div>
      {pinning && <p className="text-xs text-gray-500 mt-1">Pinning {pinning}…</p>}
      {problem && <p className="text-xs text-amber-700 mt-1">{problem}</p>}
      {open && suggestions.length > 0 && (
        <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden divide-y divide-gray-100">
          {suggestions.map((s, i) => (
            <button
              key={s.place_id || `${s.label}-${i}`}
              type="button"
              // pointerdown fires before the input's blur on every phone and mouse;
              // preventDefault keeps the keyboard where it is.
              onPointerDown={e => { e.preventDefault(); handleSelect(s); }}
              onClick={e => e.preventDefault()}
              className="w-full text-left px-3 py-3 text-sm text-gray-800 hover:bg-brand-50 active:bg-brand-100 flex items-start gap-2"
            >
              <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${i === 0 ? 'text-brand-600' : 'text-gray-500'}`} />
              <span className="min-w-0">
                <span className="block truncate font-medium">{s.address || s.label}</span>
                {secondLine(s) && <span className="block truncate text-xs text-gray-500">{secondLine(s)}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
